import { appStorage } from './betaStorage'
import type { Role } from '../types'

export type TodaySectionId = 'support_today' | 'schedule' | 'tasks' | 'routines' | 'cover' | 'updates' | 'handover'

export interface TodayPreferences {
  userId: string
  sectionIds: TodaySectionId[]
}

const STORAGE_KEY = 'schub.todayLayout.v1'

const DEFAULTS: Record<Role, TodaySectionId[]> = {
  participant: ['support_today', 'schedule', 'tasks', 'routines', 'cover'],
  rep: ['support_today', 'schedule', 'tasks', 'cover', 'updates'],
  support_worker: ['schedule', 'tasks', 'handover', 'cover', 'updates'],
  allied_health: ['schedule', 'updates'],
  coordinator: ['schedule', 'tasks', 'cover', 'updates'],
}

const ALLOWED: Record<Role, TodaySectionId[]> = {
  participant: ['support_today', 'schedule', 'tasks', 'routines', 'cover', 'updates'],
  rep: ['support_today', 'schedule', 'tasks', 'cover', 'updates'],
  support_worker: ['schedule', 'tasks', 'handover', 'cover', 'updates'],
  allied_health: ['schedule', 'updates'],
  coordinator: ['schedule', 'tasks', 'cover', 'updates'],
}

function loadAll(): TodayPreferences[] {
  try {
    const raw = appStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function allowedTodaySections(role: Role) {
  return [...ALLOWED[role]]
}

export function defaultTodaySections(role: Role) {
  return [...DEFAULTS[role]]
}

export function loadTodayPreferences(userId: string, role: Role): TodayPreferences {
  const saved = loadAll().find((item) => item.userId === userId)
  if (!saved) return { userId, sectionIds: defaultTodaySections(role) }
  const allowed = new Set(allowedTodaySections(role))
  const filtered = saved.sectionIds.filter((id) => allowed.has(id))
  // New default sections should still appear for existing saved layouts unless the user later removes them.
  if (role === 'support_worker' && !saved.sectionIds.includes('handover')) filtered.push('handover')
  return { userId, sectionIds: Array.from(new Set(filtered)) }
}

export function saveTodayPreferences(preferences: TodayPreferences) {
  const all = loadAll().filter((item) => item.userId !== preferences.userId)
  appStorage.setItem(STORAGE_KEY, JSON.stringify([...all, preferences]))
}

